import React, { useState, useEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { ChevronDown, LayoutDashboard, Briefcase, Hammer, FileSignature, Users, Wrench, Settings as SettingsIcon, Cpu } from "lucide-react";

// Sidebar groups. Items reference paths in allNavItems (Layout.jsx) so role filtering stays in one place.
export const NAV_GROUPS = [
  { key: "dashboard", label: "Dashboard", icon: LayoutDashboard, single: "/dashboard" },
  { key: "sales", label: "Sales", icon: Briefcase, paths: ["/leads", "/customers", "/estimates", "/orders"] },
  { key: "jobs", label: "Jobs", icon: Hammer, paths: ["/calendar", "/tasks", "/procurement", "/production-sheets", "/timecard", "/customer-requests"] },
  { key: "shop", label: "Shop Operations", icon: Cpu, paths: ["/cnc-router-station"] },
  { key: "billing", label: "Documents & Billing", icon: FileSignature, paths: ["/contracts", "/reviews"] },
  { key: "team", label: "Team", icon: Users, paths: ["/chat", "/team", "/suppliers", "/approvals"] },
  { key: "tools", label: "Tools", icon: Wrench, paths: ["/forms", "/automation-rules", "/analytics"] },
  { key: "settings", label: "Settings", icon: SettingsIcon, single: "/settings" },
];

export const GroupedNav = ({ navItems, onNavigate, badges = {} }) => {
  const { pathname } = useLocation();
  const byPath = Object.fromEntries(navItems.map(i => [i.to, i]));
  const activeGroup = NAV_GROUPS.find(g => g.paths?.some(p => pathname.startsWith(p)))?.key;
  const [open, setOpen] = useState(activeGroup);
  useEffect(() => { if (activeGroup) setOpen(activeGroup); }, [activeGroup]);

  return (
    <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto" data-testid="grouped-nav">
      {NAV_GROUPS.map(g => {
        if (g.single) {
          const item = byPath[g.single];
          if (!item) return null;
          return <Leaf key={g.key} item={item} onNavigate={onNavigate} badge={badges[item.to]} />;
        }
        const items = g.paths.map(p => byPath[p]).filter(Boolean);
        if (!items.length) return null;
        const isOpen = open === g.key;
        const groupBadge = items.reduce((s, i) => s + (badges[i.to] || 0), 0);
        return (
          <div key={g.key}>
            <button type="button" onClick={() => setOpen(isOpen ? null : g.key)} className={`sidebar-link w-full justify-between ${activeGroup === g.key && !isOpen ? "active" : ""}`} data-testid={`nav-group-${g.key}`}>
              <span className="flex items-center gap-3 whitespace-nowrap"><g.icon className="w-5 h-5 shrink-0" /><span>{g.label}</span></span>
              <span className="flex items-center gap-2">{groupBadge > 0 && !isOpen && <Badge n={groupBadge} />}<ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? "rotate-180" : ""}`} /></span>
            </button>
            <div className={`overflow-hidden transition-all duration-200 ${isOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0"}`}>
              <div className="ml-4 pl-3 border-l border-slate-200 space-y-0.5 py-1">
                {items.map(item => <Leaf key={item.to} item={item} onNavigate={onNavigate} badge={badges[item.to]} sub />)}
              </div>
            </div>
          </div>
        );
      })}
    </nav>
  );
};

const Leaf = ({ item, onNavigate, badge, sub }) => (
  <NavLink to={item.to} onClick={onNavigate} className={({ isActive }) => `sidebar-link ${sub ? "py-1.5 text-[13px]" : ""} ${isActive ? "active" : ""}`} data-testid={item.testId || `nav-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
    <item.icon className={sub ? "w-4 h-4" : "w-5 h-5"} />
    <span className="flex-1">{item.label}</span>
    {badge > 0 && <Badge n={badge} />}
  </NavLink>
);

const Badge = ({ n }) => <span className="text-[10px] font-semibold bg-amber-500 text-white rounded-full px-1.5 py-0.5 leading-none" data-testid="nav-badge">{n}</span>;
